import { DFASearchParams } from '@mhe/reader/models';

export type InitialNavigation =
| { type: 'cfi', cfi: string }
| { type: 'dfaSearch', params: DFASearchParams }
| { type: 'scoresheet' }
| { type: 'spineId', id: string, hash?: string }
| { type: 'default' };

interface InitialNavigationParams {
  pageRangeCfi: string | undefined
  isMatchingPageRange: number | boolean | undefined
  dfaSearchLaunchParams: Partial<DFASearchParams>
  scoresheet: boolean
  cfi: string | undefined
  // Caller is responsible for pre-resolving TOC chapter hrefs/anchors to a
  // spine id before passing here. Pass undefined when no LTI spine override.
  customSpineId: string | undefined
  routeSpineId: string | undefined
  routeHash: string | undefined
  lastLocation: string | null | undefined
}

// Centralizes first-load navigation precedence so init logic is deterministic.
// Order matters: explicit launch params win over persisted last-location state.
export function getInitialNavigation({
  pageRangeCfi,
  isMatchingPageRange,
  dfaSearchLaunchParams,
  scoresheet,
  cfi,
  customSpineId,
  routeSpineId,
  routeHash,
  lastLocation,
}: InitialNavigationParams): InitialNavigation {
  // Assignment page-range navigation has highest priority when the CFI resolves.
  if (pageRangeCfi && isMatchingPageRange) {
    return { type: 'cfi', cfi: pageRangeCfi };
  }

  if (
    dfaSearchLaunchParams.spineID &&
    dfaSearchLaunchParams.text &&
    dfaSearchLaunchParams.index !== undefined &&
    dfaSearchLaunchParams.index > -1
  ) {
    return {
      type: 'dfaSearch',
      params: {
        spineID: dfaSearchLaunchParams.spineID,
        index: dfaSearchLaunchParams.index,
        text: dfaSearchLaunchParams.text,
      },
    };
  }

  // Scoresheet review mode always resumes from the reviewed page flow.
  if (scoresheet) {
    return { type: 'scoresheet' };
  }

  // Route parameters are explicit user intent and override generic launch params.
  if (routeSpineId) {
    return { type: 'spineId', id: routeSpineId, hash: routeHash };
  }

  if (cfi) {
    return { type: 'cfi', cfi };
  }

  if (customSpineId) {
    return { type: 'spineId', id: customSpineId };
  }

  // Keep legacy fallback until Firebase 'null' payloads are fully removed.
  if (lastLocation && lastLocation !== 'null') {
    return { type: 'cfi', cfi: lastLocation };
  }

  return { type: 'default' };
}
